"use client";

import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { safeHttpUrl } from "@/lib/url";

export type CarouselImage = {
  /** Already sanitised (http(s) only) by the caller. */
  url: string;
  focus_x: number;
  focus_y: number;
  attribution: { name: string; href: string } | null;
};

/**
 * The public offering page's hero. One image renders exactly like the classic
 * single hero (no controls, no reflow); two or more become a swipeable
 * scroll-snap carousel with arrows, dots, and a counter. Slides keep the
 * 16:9 slot and per-image focus point, so the layout is identical to the
 * single-image page in every state.
 */
export function HeroCarousel({
  images,
  className,
}: {
  images: CarouselImage[];
  className?: string;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [idx, setIdx] = useState(0);

  if (images.length === 0) return null;

  if (images.length === 1) {
    const img = images[0];
    return (
      <div
        className={cn(
          "relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-muted",
          className
        )}
        style={{
          backgroundImage: `url(${img.url})`,
          backgroundSize: "cover",
          backgroundPosition: `${img.focus_x * 100}% ${img.focus_y * 100}%`,
        }}
      >
        <Attribution attribution={img.attribution} />
      </div>
    );
  }

  const scrollTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(images.length - 1, i));
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Photos"
      className={cn("group relative overflow-hidden rounded-2xl", className)}
    >
      <div
        ref={trackRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          const next = Math.round(el.scrollLeft / el.clientWidth);
          if (next !== idx) setIdx(next);
        }}
        className={cn(
          "flex aspect-[16/9] w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain",
          "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        )}
      >
        {images.map((img, i) => (
          <div
            key={`${img.url}-${i}`}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${images.length}`}
            className="relative h-full w-full shrink-0 snap-center bg-muted"
            style={{
              backgroundImage: `url(${img.url})`,
              backgroundSize: "cover",
              backgroundPosition: `${img.focus_x * 100}% ${img.focus_y * 100}%`,
            }}
          >
            <Attribution attribution={img.attribution} />
          </div>
        ))}
      </div>

      {/* Arrows — hover/focus affordance on pointer devices; swiping covers touch */}
      <CarouselArrow
        dir="prev"
        disabled={idx === 0}
        onClick={() => scrollTo(idx - 1)}
      />
      <CarouselArrow
        dir="next"
        disabled={idx === images.length - 1}
        onClick={() => scrollTo(idx + 1)}
      />

      {/* Dots */}
      <div className="pointer-events-none absolute inset-x-0 bottom-2.5 flex items-center justify-center gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to photo ${i + 1}`}
            aria-current={i === idx}
            onClick={() => scrollTo(i)}
            className={cn(
              "pointer-events-auto h-1.5 rounded-full transition-all duration-200",
              i === idx ? "w-4 bg-white" : "w-1.5 bg-white/55 hover:bg-white/80"
            )}
          />
        ))}
      </div>

      {/* Counter */}
      <span className="pointer-events-none absolute bottom-2 right-2.5 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-medium tabular-nums text-white/90 backdrop-blur-[2px]">
        {idx + 1} / {images.length}
      </span>
    </section>
  );
}

function CarouselArrow({
  dir,
  disabled,
  onClick,
}: {
  dir: "prev" | "next";
  disabled: boolean;
  onClick: () => void;
}) {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={dir === "prev" ? "Previous photo" : "Next photo"}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full",
        "bg-white/90 text-foreground shadow-[0_1px_6px_rgba(0,0,0,0.25)]",
        "transition-opacity duration-150",
        "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
        disabled && "group-hover:opacity-40",
        dir === "prev" ? "left-2.5" : "right-2.5"
      )}
    >
      <Icon className="size-4" />
    </button>
  );
}

function Attribution({
  attribution,
}: {
  attribution: { name: string; href: string } | null;
}) {
  // The DB can be written around the app's zod validation (direct PostgREST
  // insert, RPC payload), so the href must be re-checked here at the render
  // site — an unsafe scheme drops the credit rather than linking it.
  const href = attribution ? safeHttpUrl(attribution.href) : null;
  if (!attribution || !href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        "absolute bottom-2 left-2 rounded px-1.5 py-0.5",
        "text-[10px] leading-none text-white/80",
        "bg-black/25 backdrop-blur-[2px]",
        "opacity-60 transition-opacity hover:text-white hover:opacity-100"
      )}
    >
      {attribution.name} / Unsplash
    </a>
  );
}
